import { LanguageType } from '../core';
import { IdTokenPayload } from './idTokenPayload';
import { LogoutReason } from './logoutReason';

export type AuthSecrets = {
  JwtToken: string;
  idToken: string;
  IdPayload: IdTokenPayload;
  refreshToken?: string | null;
};

export enum AuthState {
  /** state is not yet initialized */
  Unknown = 'Unknown',
  /** user settings, session init, etc are loading */
  Loading = 'Loading',
  /** user is authenticated and everything is loaded */
  Authenticated = 'Authenticated',
  /** user is not authenticated */
  NotAuthenticated = 'NotAuthenticated',
  /** user needs to be signed off */
  SignOff = 'SignOff',
}

export enum UnknownAppStage {
  SSOLoading = 'SSOLoading',
  InitSSEManager = 'InitSSEManager',
}

/**
 * AuthStage is used to track the progress of the authentication process
 */
export enum AuthStage {
  Step_SetPin = 'Step_SetPin',
  Step_SetBiometric = 'Step_SetBiometric',
  Step1_LoadUserSettings = 'Step1_LoadUserSettings',
  Step2_LoadSessionInit = 'Step2_LoadSessionInit',
  Step3_LoadConsents = 'Step3_LoadConsents',
  Step4_LoadAppData = 'Step4_LoadAppData',

  OtherInstanceLoginInProgress = 'OtherInstanceLoginInProgress',
}

/**
 * State for the phase when we are waiting for OIDC initialization OR multi tab login
 */
type UnknownAppState = {
  isAuthenticated: false;
  authStatus: AuthState.Unknown;
  stage: UnknownAppStage;
  error?: LogoutReason;
  errorDetails?: string;
};

type UnAuthenticatedAppState = {
  isAuthenticated: false;
  authStatus: AuthState.NotAuthenticated | AuthState.Loading | AuthState.SignOff;
  errorDetails?: string;
  error?: LogoutReason;
};

export type AuthenticatedAppState = {
  isAuthenticated: true;
  authStatus: AuthState.Loading;
  stage: AuthStage;
  isRefresh?: boolean;
  language?: LanguageType;
  secrets: AuthSecrets;
};

export type AuthenticatedLoadingAppState = {
  isAuthenticated: true;
  authStatus: AuthState.Authenticated;
  language?: LanguageType;
  secrets: AuthSecrets;
};

/** This state marks that the user should be signed off */
type SignOffAppState = {
  isAuthenticated: true;
  authStatus: AuthState.SignOff;
  language?: LanguageType;
  error?: LogoutReason;
  errorDetails?: string;
  secrets: AuthSecrets;
};

export type AppAuthState =
  | AuthenticatedAppState
  | AuthenticatedLoadingAppState
  | UnAuthenticatedAppState
  | UnknownAppState
  | SignOffAppState;
